import { Box, CircularProgress, DialogContent, Divider, List, Typography } from "@mui/material"
import { HomeWorkOutlined, SearchOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { useCustomerSearch } from "../../hooks/useCustomerSearch"
import type { SalesCustomer } from "../../types"

import { CustomerResultItem } from "./CustomerResultItem"
import { CustomerSearchSuggestion } from "./CustomerSearchSuggestion"
import { NewRequestStepActions } from "./NewRequestStepActions"
import { StepSectionLabel } from "./StepSectionLabel"

import { Button } from "@/components/common/Button"

interface CustomerSearchStepProps {
  onCancel: () => void
  onContinue: (customer: SalesCustomer) => void
}

export const CustomerSearchStep: FC<CustomerSearchStepProps> = ({ onCancel, onContinue }) => {
  const {
    searchInputValue,
    setSearchInputValue,
    customers,
    isFetching,
    showSearchResults,
    selectedCustomer,
    setSelectedCustomer,
    handleSearch,
    handleCustomerSuggestionSelect,
    handleResultsScroll,
  } = useCustomerSearch()

  const handleContinue = () => {
    if (selectedCustomer) onContinue(selectedCustomer)
  }

  return (
    <>
      <DialogContent className='flex flex-col gap-6 px-8 py-6'>
        <StepSectionLabel icon={<HomeWorkOutlined />} label='Search for a customer' />

        <Box className='flex gap-2'>
          <CustomerSearchSuggestion
            searchInputValue={searchInputValue}
            onSearchInputChange={setSearchInputValue}
            onSearch={handleSearch}
            onCustomerSelect={handleCustomerSuggestionSelect}
          />
          <Button
            appearance='contained'
            startIcon={<SearchOutlined />}
            onClick={() => handleSearch(searchInputValue)}
            disabled={searchInputValue.trim().length < 2}
            data-testid='sales.customer-search-step.search-button'
          >
            Search
          </Button>
        </Box>

        {showSearchResults && isFetching && customers.length === 0 && (
          <Box className='flex justify-center py-2'>
            <CircularProgress size={24} />
          </Box>
        )}

        {showSearchResults && !isFetching && customers.length === 0 && (
          <Typography className='text-sm text-gray-500'>No results found. Try a different search term.</Typography>
        )}

        {showSearchResults && customers.length > 0 && (
          <>
            <Typography className='mt-2 inline-flex align-middle text-sm leading-6'>
              <Typography component='span' className='inline-flex font-semibold text-gray-800'>
                {customers.length} results found
              </Typography>
              , Select one from the existing
            </Typography>
            <Box onScroll={handleResultsScroll} className='max-h-80 overflow-y-auto rounded border border-gray-300'>
              <List disablePadding>
                {customers.map((customer, index) => (
                  <Box key={customer.companyLocationAssociationId}>
                    {index > 0 && <Divider />}
                    <CustomerResultItem
                      customer={customer}
                      selected={
                        selectedCustomer?.companyLocationAssociationId === customer.companyLocationAssociationId
                      }
                      onSelect={setSelectedCustomer}
                    />
                  </Box>
                ))}
              </List>
            </Box>
          </>
        )}
      </DialogContent>

      <NewRequestStepActions
        onCancel={onCancel}
        onContinue={handleContinue}
        continueDisabled={!selectedCustomer}
        cancelTestId='sales.customer-search-step.cancel-button'
        continueTestId='sales.customer-search-step.continue-button'
      />
    </>
  )
}
